/**
 * Asset Links Hook
 *
 * React hook for managing asset trackable links and QR codes
 * with GraphQL integration and state management.
 */

import { useState, useCallback, useEffect } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  AssetLink,
  AssetLinkConnection,
  AssetLinkFilterInput,
  GenerateAssetLinkInput,
  UpdateAssetLinkInput,
  GenerateQRCodeInput,
  UploadCustomQRInput,
  AssetLinkAnalytics,
  QRCodeResult,
  CustomQRUploadResult,
} from "../types/asset-link-types";
import {
  LIST_ASSET_LINKS,
  GET_ASSET_LINK,
  GET_ASSET_LINK_ANALYTICS,
  GENERATE_ASSET_LINK,
  UPDATE_ASSET_LINK,
  DELETE_ASSET_LINK,
  GENERATE_QR_CODE,
  DELETE_QR_CODE,
  UPLOAD_CUSTOM_QR,
  DELETE_CUSTOM_QR,
  GENERATE_QR_CODE_DOWNLOAD_URL,
} from "../services/graphql/asset-link-queries";

interface UseAssetLinksOptions {
  assetId?: string;
  filter?: AssetLinkFilterInput;
  limit?: number;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export function useAssetLinks(options: UseAssetLinksOptions = {}) {
  const {
    assetId,
    filter,
    limit = 50,
    autoRefresh = false,
    refreshInterval = 30000,
  } = options;

  // State
  const [assetLinks, setAssetLinks] = useState<AssetLink[]>([]);
  const [total, setTotal] = useState(0);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);

  // Queries
  const {
    data: assetLinksData,
    loading: assetLinksLoading,
    error: assetLinksError,
    refetch: refetchAssetLinks,
    fetchMore: fetchMoreAssetLinks,
  } = useQuery(LIST_ASSET_LINKS, {
    variables: {
      assetId,
      filter,
      limit,
    },
    skip: !assetId,
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      if (data?.listAssetLinks) {
        setAssetLinks(data.listAssetLinks.items);
        setTotal(data.listAssetLinks.total);
        setNextToken(data.listAssetLinks.nextToken);
        setHasMore(!!data.listAssetLinks.nextToken);
      }
    },
  });

  const [getAssetLink, { loading: getAssetLinkLoading }] =
    useLazyQuery(GET_ASSET_LINK);

  const [getAssetLinkAnalytics, { loading: analyticsLoading }] = useLazyQuery(
    GET_ASSET_LINK_ANALYTICS
  );

  const [generateQRCodeDownloadUrl] = useLazyQuery(
    GENERATE_QR_CODE_DOWNLOAD_URL
  );

  // Mutations
  const [generateAssetLinkMutation, { loading: generateLoading }] = useMutation(
    GENERATE_ASSET_LINK,
    {
      onCompleted: () => {
        refetchAssetLinks();
      },
    }
  );

  const [updateAssetLinkMutation, { loading: updateLoading }] = useMutation(
    UPDATE_ASSET_LINK,
    {
      onCompleted: () => {
        refetchAssetLinks();
      },
    }
  );

  const [deleteAssetLinkMutation, { loading: deleteLoading }] = useMutation(
    DELETE_ASSET_LINK,
    {
      onCompleted: () => {
        refetchAssetLinks();
      },
    }
  );

  const [generateQRCodeMutation, { loading: generateQRLoading }] = useMutation(
    GENERATE_QR_CODE,
    {
      onCompleted: () => {
        refetchAssetLinks();
      },
    }
  );

  const [deleteQRCodeMutation, { loading: deleteQRLoading }] = useMutation(
    DELETE_QR_CODE,
    {
      onCompleted: () => {
        refetchAssetLinks();
      },
    }
  );

  const [uploadCustomQRMutation, { loading: uploadCustomQRLoading }] =
    useMutation(UPLOAD_CUSTOM_QR, {
      onCompleted: () => {
        refetchAssetLinks();
      },
    });

  const [deleteCustomQRMutation, { loading: deleteCustomQRLoading }] =
    useMutation(DELETE_CUSTOM_QR, {
      onCompleted: () => {
        refetchAssetLinks();
      },
    });

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh && assetId) {
      const interval = setInterval(() => {
        refetchAssetLinks();
      }, refreshInterval);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [autoRefresh, assetId, refreshInterval, refetchAssetLinks]);

  // Actions
  const generateAssetLink = useCallback(
    async (input: GenerateAssetLinkInput): Promise<AssetLink> => {
      const result = await generateAssetLinkMutation({
        variables: { input },
      });
      return result.data.generateAssetLink;
    },
    [generateAssetLinkMutation]
  );

  const updateAssetLink = useCallback(
    async (id: string, input: UpdateAssetLinkInput): Promise<AssetLink> => {
      const result = await updateAssetLinkMutation({
        variables: { id, input },
      });
      return result.data.updateAssetLink;
    },
    [updateAssetLinkMutation]
  );

  const deleteAssetLink = useCallback(
    async (id: string): Promise<boolean> => {
      const result = await deleteAssetLinkMutation({
        variables: { id },
      });
      return result.data.deleteAssetLink;
    },
    [deleteAssetLinkMutation]
  );

  const generateQRCode = useCallback(
    async (input: GenerateQRCodeInput): Promise<QRCodeResult> => {
      const result = await generateQRCodeMutation({
        variables: { input },
      });

      if (!result.data?.generateQRCode) {
        throw new Error("Failed to generate QR code: No data returned");
      }

      return result.data.generateQRCode;
    },
    [generateQRCodeMutation]
  );

  const deleteQRCode = useCallback(
    async (id: string): Promise<boolean> => {
      const result = await deleteQRCodeMutation({
        variables: { id },
      });
      return result.data.deleteQRCode;
    },
    [deleteQRCodeMutation]
  );

  const uploadCustomQR = useCallback(
    async (input: UploadCustomQRInput): Promise<CustomQRUploadResult> => {
      const result = await uploadCustomQRMutation({
        variables: { input },
      });
      return result.data.uploadCustomQR;
    },
    [uploadCustomQRMutation]
  );

  const deleteCustomQR = useCallback(
    async (id: string): Promise<boolean> => {
      const result = await deleteCustomQRMutation({
        variables: { id },
      });
      return result.data.deleteCustomQR;
    },
    [deleteCustomQRMutation]
  );

  const getAnalytics = useCallback(
    async (assetId: string): Promise<AssetLinkAnalytics> => {
      const result = await getAssetLinkAnalytics({
        variables: { assetId },
      });
      return result.data.getAssetLinkAnalytics;
    },
    [getAssetLinkAnalytics]
  );

  const loadMore = useCallback(async () => {
    if (hasMore && nextToken && !assetLinksLoading) {
      const result = await fetchMoreAssetLinks({
        variables: {
          nextToken,
        },
      });

      if (result.data?.listAssetLinks) {
        setAssetLinks((prev) => [...prev, ...result.data.listAssetLinks.items]);
        setNextToken(result.data.listAssetLinks.nextToken);
        setHasMore(!!result.data.listAssetLinks.nextToken);
      }
    }
  }, [hasMore, nextToken, assetLinksLoading, fetchMoreAssetLinks]);

  const refresh = useCallback(() => {
    refetchAssetLinks();
  }, [refetchAssetLinks]);

  return {
    // Data
    assetLinks,
    total,
    hasMore,

    // Loading states
    loading: assetLinksLoading,
    generateLoading,
    updateLoading,
    deleteLoading,

    generateQRLoading,
    deleteQRLoading,
    uploadCustomQRLoading,
    deleteCustomQRLoading,
    analyticsLoading,
    getAssetLinkLoading,

    // Errors
    error: assetLinksError,

    // Actions
    generateAssetLink,
    updateAssetLink,
    deleteAssetLink,

    generateQRCode,
    deleteQRCode,
    uploadCustomQR,
    deleteCustomQR,
    getAnalytics,
    loadMore,
    refresh,
  };
}
